// src/components/ModalDocumentosRemito.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

import {
  getDocumentosRemito,
  subirDocumentoRemito,
  eliminarDocumentoRemito,
} from "../helpers/rutaRemitoDocumentos";

const clean = (v) => String(v ?? "").trim();

const docVacio = {
  url: "",
  publicId: "",
  resourceType: "",
  nombreOriginal: "",
  mimeType: "",
  size: 0,
  fechaSubida: null,
};

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return "-";

  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;

  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (v) => {
  if (!v) return "-";

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-AR");
};

const fmtFechaRemito = (v) => {
  if (!v) return "-";

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-AR");
};

const normalizarDoc = (doc = {}) => {
  const d = doc || {};

  return {
    ...docVacio,
    ...d,
    url: clean(d.url || d.secure_url || d.secureUrl),
    publicId: clean(d.publicId || d.public_id),
    resourceType: clean(d.resourceType || d.resource_type || "image"),
    nombreOriginal: clean(
      d.nombreOriginal ||
        d.originalname ||
        d.original_name ||
        d.filename ||
        d.nombre ||
        ""
    ),
    mimeType: clean(d.mimeType || d.mimetype || d.type || ""),
    size: Number(d.size || d.bytes || 0),
    fechaSubida: d.fechaSubida || d.created_at || d.createdAt || null,
  };
};

const normalizarDocumentos = (docs = {}) => {
  const d = docs || {};

  return {
    ...d,
    ticketBalanza: normalizarDoc(d.ticketBalanza),
    dtvCompra: normalizarDoc(d.dtvCompra),
    dtvVenta: normalizarDoc(d.dtvVenta),
  };
};

const normalizarRemito = (remito) => {
  if (!remito) return null;

  return {
    ...remito,
    documentos: normalizarDocumentos(remito.documentos || {}),
  };
};

const isImage = (doc = {}) => {
  const safeDoc = normalizarDoc(doc);

  return (
    String(safeDoc.mimeType || "").startsWith("image/") ||
    /\.(jpg|jpeg|png|webp)$/i.test(String(safeDoc.url || ""))
  );
};

const getRemitoFromResp = (resp, fallback) => {
  if (resp?.remito) return normalizarRemito(resp.remito);
  if (resp?.data?.remito) return normalizarRemito(resp.data.remito);
  if (resp?.data) return normalizarRemito(resp.data);
  return normalizarRemito(fallback);
};

const nombreEntidad = (obj) => {
  if (!obj) return "-";
  if (typeof obj === "string") return obj;

  return clean(obj.razonsocial || obj.nombre || obj.descripcion) || "-";
};

const DocumentoCard = ({
  titulo,
  tipo,
  ayuda,
  accept,
  doc,
  uploading,
  deleting,
  onUpload,
  onDelete,
}) => {
  const safeDoc = normalizarDoc(doc);
  const tieneArchivo = !!safeDoc.url;

  return (
    <div className="border rounded p-3 mb-3" style={{ background: "#fafafa" }}>
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {ayuda}
          </div>
        </div>

        <span
          className={`badge ${tieneArchivo ? "bg-success" : "bg-secondary"}`}
        >
          {tieneArchivo ? "Cargado" : "Pendiente"}
        </span>
      </div>

      {tieneArchivo ? (
        <div className="mb-2">
          {isImage(safeDoc) ? (
            <a href={safeDoc.url} target="_blank" rel="noreferrer">
              <img
                src={safeDoc.url}
                alt={titulo}
                style={{
                  width: "100%",
                  maxHeight: 240,
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </a>
          ) : (
            <div
              className="border rounded p-3 text-center"
              style={{ background: "#fff" }}
            >
              <i
                className="fa fa-file-o"
                aria-hidden="true"
                style={{ fontSize: 38, color: "#444" }}
              />
              <div className="mt-2">
                <a href={safeDoc.url} target="_blank" rel="noreferrer">
                  Ver archivo
                </a>
              </div>
            </div>
          )}

          <div className="mt-2" style={{ fontSize: 12 }}>
            <div>
              <strong>Archivo:</strong> {safeDoc.nombreOriginal || "-"}
            </div>
            <div>
              <strong>Tamaño:</strong> {fmtSize(safeDoc.size)}
            </div>
            <div>
              <strong>Fecha:</strong> {fmtDate(safeDoc.fechaSubida)}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          Todavía no se cargó archivo.
        </div>
      )}

      <div className="d-flex gap-2 flex-wrap">
        <label className="btn btn-primary btn-sm mb-0">
          {uploading ? "Subiendo..." : tieneArchivo ? "Reemplazar" : "Subir"}
          <input
            type="file"
            accept={accept}
            hidden
            disabled={uploading || deleting}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";

              if (file) {
                onUpload(tipo, file);
              }
            }}
          />
        </label>

        {tieneArchivo && (
          <>
            <a
              className="btn btn-outline-secondary btn-sm"
              href={safeDoc.url}
              target="_blank"
              rel="noreferrer"
            >
              Ver
            </a>

            <Button
              variant="outline-danger"
              size="sm"
              disabled={uploading || deleting}
              onClick={() => onDelete(tipo)}
            >
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default function ModalDocumentosRemito({
  show,
  remito,
  onHide,
  onRemitoActualizado,
}) {
  const [localRemito, setLocalRemito] = useState(normalizarRemito(remito));
  const [loading, setLoading] = useState(false);
  const [loadingTipo, setLoadingTipo] = useState("");
  const [deletingTipo, setDeletingTipo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const cargar = async () => {
      if (!show || !remito?._id) {
        setLocalRemito(normalizarRemito(remito));
        return;
      }

      try {
        setError("");
        setLoading(true);
        setLoadingTipo("");
        setDeletingTipo("");

        const resp = await getDocumentosRemito(remito._id);

        if (!mounted) return;

        const actualizado = getRemitoFromResp(resp, remito);
        setLocalRemito(actualizado);
      } catch (e) {
        console.error("❌ Error cargando documentos del remito:", e);

        if (!mounted) return;

        setLocalRemito(normalizarRemito(remito));
        setError(
          e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.message ||
            "No se pudieron cargar los documentos del remito."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    cargar();

    return () => {
      mounted = false;
    };
  }, [show, remito?._id]);

  const documentos = useMemo(
    () => normalizarDocumentos(localRemito?.documentos || {}),
    [localRemito]
  );

  const handleUpload = async (tipo, file) => {
    try {
      setError("");

      if (!localRemito?._id) {
        setError("No se encontró el ID del remito.");
        return;
      }

      const permitidos = ["image/jpeg", "image/jpg", "image/png"];

      if (!permitidos.includes(file.type)) {
        setError("Solo se permiten imágenes JPG o PNG.");
        return;
      }

      const max = 3 * 1024 * 1024;

      if (file.size > max) {
        setError("El archivo supera los 3 MB permitidos.");
        return;
      }

      setLoadingTipo(tipo);

      const resp = await subirDocumentoRemito(localRemito._id, tipo, file);

      const remitoResp = getRemitoFromResp(resp, localRemito);
      const documentoResp = normalizarDoc(
        resp?.documento || resp?.data?.documento || {}
      );

      const documentosActualizados = normalizarDocumentos({
        ...(localRemito?.documentos || {}),
        ...(remitoResp?.documentos || {}),
      });

      if (documentoResp.url) {
        documentosActualizados[tipo] = documentoResp;
      }

      const actualizado = normalizarRemito({
        ...localRemito,
        ...remitoResp,
        documentos: documentosActualizados,
      });

      setLocalRemito(actualizado);

      if (onRemitoActualizado) {
        onRemitoActualizado(actualizado);
      }
    } catch (e) {
      console.error("❌ Error subiendo documento:", e);

      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo subir el documento."
      );
    } finally {
      setLoadingTipo("");
    }
  };

  const handleDelete = async (tipo) => {
    if (!localRemito?._id) {
      setError("No se encontró el ID del remito.");
      return;
    }

    if (!window.confirm("¿Eliminar este documento del remito?")) return;

    try {
      setError("");
      setDeletingTipo(tipo);

      const resp = await eliminarDocumentoRemito(localRemito._id, tipo);
      const remitoResp = getRemitoFromResp(resp, localRemito);

      const documentosActualizados = normalizarDocumentos({
        ...(localRemito?.documentos || {}),
        ...(remitoResp?.documentos || {}),
        [tipo]: { ...docVacio },
      });

      const actualizado = normalizarRemito({
        ...localRemito,
        ...remitoResp,
        documentos: documentosActualizados,
      });

      setLocalRemito(actualizado);

      if (onRemitoActualizado) {
        onRemitoActualizado(actualizado);
      }
    } catch (e) {
      console.error("❌ Error eliminando documento:", e);

      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo eliminar el documento."
      );
    } finally {
      setDeletingTipo("");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Documentos del Remito</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && !localRemito ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Cargando documentos...</div>
          </div>
        ) : !localRemito ? (
          <div className="alert alert-warning">
            No se encontró el remito seleccionado.
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div style={{ fontWeight: 700, fontSize: 17 }}>
                Remito N° {localRemito.codrem || "-"}
              </div>

              <div className="text-muted" style={{ fontSize: 13 }}>
                Fecha: {fmtFechaRemito(localRemito.fecha)} | Cliente:{" "}
                {nombreEntidad(localRemito.cliente)} | Proveedor:{" "}
                {nombreEntidad(localRemito.proveedor)}
              </div>
            </div>

            {error && (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                {error}
              </div>
            )}

            <DocumentoCard
              titulo="Ticket de balanza"
              tipo="ticketBalanza"
              ayuda="Imagen del ticket de balanza. Formatos: JPG o PNG."
              accept="image/jpeg,image/jpg,image/png"
              doc={documentos.ticketBalanza}
              uploading={loadingTipo === "ticketBalanza"}
              deleting={deletingTipo === "ticketBalanza"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />

            <DocumentoCard
              titulo="DTV compra"
              tipo="dtvCompra"
              ayuda="Imagen del DTV de compra. Formatos: JPG o PNG."
              accept="image/jpeg,image/jpg,image/png"
              doc={documentos.dtvCompra}
              uploading={loadingTipo === "dtvCompra"}
              deleting={deletingTipo === "dtvCompra"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />

            <DocumentoCard
              titulo="DTV venta"
              tipo="dtvVenta"
              ayuda="Imagen del DTV de venta. Formatos: JPG o PNG."
              accept="image/jpeg,image/jpg,image/png"
              doc={documentos.dtvVenta}
              uploading={loadingTipo === "dtvVenta"}
              deleting={deletingTipo === "dtvVenta"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}